import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, filter, firstValueFrom } from 'rxjs';

import { DialogAprovarAcessoComponent } from 'src/app/modals/dialog-aprovar-acesso/dialog-aprovar-acesso.component';
import { CredenciaisAutorizacao, LARGURA_MODAL_AUTORIZAR_ACESSO, ResultadoAutorizacao } from 'src/app/models/User/AprovarAcesso.model';
import {
    AcaoPermissao, CodigoAutorizacaoWeb, MinhaAcaoPermissao, NomeFuncionalidade, mensagemDoCodigoDeAutorizacao, TelasDoMenu, TipoAutorizacaoWeb, TokenAutorizacao,
    temRotaProtegida, tipoDaFuncionalidade
} from 'src/app/models/User/MinhaAcaoPermissao.model';
import { AlertMessageService } from '../alert-message/alert-message.service';
import { ApiErrorResponse } from '../api/api.service';
import { SessionService } from '../session/session.service';
import { AutorizacaoTokenStore } from './autorizacao-token.store';
import { AutorizacaoService } from './autorizacao.service';
import { registrarFalhaDeAutorizacao } from './registrar-falha-autorizacao';

// Mesmas regras do PermissaoController.verificarAcesso do PDV. A API valida de novo nas rotas protegidas.
@Injectable({
    providedIn: 'root'
})
export class PermissaoAcessoService {
    // Autorização de tela já dada por funcionalidade: a rota em que ela continua valendo
    private autorizadores = new Map<number, string>();

    // Permissões do usuário logado (GET v1/permissao/minhas), mantidas em memória
    private permissoes = new Map<number, AcaoPermissao>();
    private ehAdministrador = false;
    private permissoesCarregadas = false;
    private carregamento?: Promise<boolean>;

    // Verdadeiro quando as permissões não puderam ser carregadas: o painel não exibe telas protegidas (falha fechada)
    private falhaAoCarregarSubject = new BehaviorSubject<boolean>(false);
    readonly falhaAoCarregar$ = this.falhaAoCarregarSubject.asObservable();

    constructor(
        private autorizacaoService: AutorizacaoService,
        private tokenStore: AutorizacaoTokenStore,
        private sessionService: SessionService,
        private alertMessage: AlertMessageService,
        private translate: TranslateService,
        private dialog: MatDialog,
        private router: Router
    ) {
        // Ao sair da rota onde a autorização foi dada (ou deslogar) a senha volta a ser pedida
        this.router.events
            .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
            .subscribe(event => this.descartarAutorizacoesForaDaRota(event.urlAfterRedirects));
    }

    private descartarAutorizacoesForaDaRota(url: string): void {
        if (!this.sessionService.isLogged) {
            this.autorizadores.clear();
            this.tokenStore.limpar();
            this.permissoes.clear();
            this.permissoesCarregadas = false;
            return;
        }

        this.autorizadores.forEach((rota, funcionalidadeId) => {
            const continuaNaRota = url === rota
                || url.startsWith(rota + '/')
                || url.startsWith(rota + '?');

            if (!continuaNaRota) {
                this.autorizadores.delete(funcionalidadeId);
                // O token de leitura vale enquanto a tela estiver aberta
                this.tokenStore.remover(funcionalidadeId);
            }
        });
    }

    private rotaAtual(): string {
        return this.router.url.split('?')[0];
    }

    // Nome traduzido da funcionalidade, para as mensagens e o modal
    private nomeDa(funcionalidadeId: number): string {
        return this.translate.instant(NomeFuncionalidade[funcionalidadeId]);
    }

    /**
     * Carrega as permissões do usuário logado. Sem forçar, só consulta a API se ainda não carregou.
     * Devolve false se não conseguir (e então o painel mostra o erro com "Tentar novamente").
     */
    public carregarPermissoes(forcar: boolean = false): Promise<boolean> {
        if (!forcar && this.permissoesCarregadas) return Promise.resolve(true);

        // Chamadas ao mesmo tempo dividem a mesma consulta
        if (!this.carregamento) {
            this.carregamento = this.buscarPermissoes().finally(() => this.carregamento = undefined);
        }

        return this.carregamento;
    }

    private async buscarPermissoes(): Promise<boolean> {
        try {
            const minhas = await this.autorizacaoService.getMinhasPermissoes();

            this.ehAdministrador = minhas.isAdministrator;
            this.permissoes = new Map(minhas.permissoes.map(item => [item.funcionalidadeSistemaId, item.acao]));
            this.permissoesCarregadas = true;
            this.falhaAoCarregarSubject.next(false);
            return true;
        } catch (erro) {
            registrarFalhaDeAutorizacao(erro, 'Falha ao carregar as permissões do usuário');
            this.permissoesCarregadas = false;
            this.falhaAoCarregarSubject.next(true);
            return false;
        }
    }

    // Null quando as permissões não puderam ser carregadas
    public async obterPermissao(funcionalidadeId: number): Promise<MinhaAcaoPermissao | null> {
        if (!(await this.carregarPermissoes())) return null;

        return {
            funcionalidadeSistemaId: funcionalidadeId,
            // Funcionalidade sem vínculo = Autorizar (mesma regra da API e do PDV)
            acao: this.permissoes.get(funcionalidadeId) ?? AcaoPermissao.Autorizar,
            isAdministrator: this.ehAdministrador
        };
    }

    // Funcionalidade de gravação cuja rota exige token de uso único: pede a senha a cada gravação
    private ehGravacaoPorAcao(funcionalidadeId: number): boolean {
        return tipoDaFuncionalidade(funcionalidadeId) === TipoAutorizacaoWeb.Gravacao && temRotaProtegida(funcionalidadeId);
    }

    private jaAutorizada(funcionalidadeId: number): boolean {
        return this.ehGravacaoPorAcao(funcionalidadeId)
            ? this.tokenStore.temTokenValido(funcionalidadeId)
            : this.autorizadores.has(funcionalidadeId);
    }

    // Responde se entra direto (admin, ação "Autorizar" ou já autorizado), sem abrir modal
    public async podeEntrarSemSenha(funcionalidadeId: number): Promise<boolean> {
        if (this.jaAutorizada(funcionalidadeId)) return true;

        const permissao = await this.obterPermissao(funcionalidadeId);
        if (!permissao) return false;

        return permissao.isAdministrator || permissao.acao === AcaoPermissao.Autorizar;
    }

    // Menu, aba ou botão deve sumir só na ação "Bloquear e ocultar"; nas demais o clique decide.
    // Sem conseguir carregar as permissões, esconde (falha fechada).
    public async estaOculta(funcionalidadeId: number): Promise<boolean> {
        const permissao = await this.obterPermissao(funcionalidadeId);
        if (!permissao) return true;

        return !permissao.isAdministrator && permissao.acao === AcaoPermissao.BloquearOcultar;
    }

    // Primeira tela do menu que o usuário abre sem pedir senha, exceto a que está bloqueada. Sem nenhuma, a home.
    public async primeiraTelaLiberada(rotaBloqueada: string): Promise<string> {
        // Sem permissões carregadas, cada tela tentaria a consulta de novo: vai direto para a home
        if (!(await this.carregarPermissoes())) return '/home';

        for (const tela of TelasDoMenu) {
            if (rotaBloqueada.startsWith(tela.url)) continue;
            if (await this.podeEntrarSemSenha(tela.funcionalidadeId)) return tela.url;
        }
        return '/home';
    }

    /**
     * Aplica a ação configurada no grupo do usuário para a funcionalidade.
     * rota: onde a autorização continua valendo (padrão: a tela atual, para botões/ações).
     */
    public async verificarAcesso(funcionalidadeId: number, rota: string = this.rotaAtual()): Promise<boolean> {
        if (this.jaAutorizada(funcionalidadeId)) return true;

        const permissao = await this.obterPermissao(funcionalidadeId);
        if (!permissao) return false;

        if (permissao.isAdministrator) return true;

        switch (permissao.acao) {
            case AcaoPermissao.BloquearOcultar:
                this.alertMessage.alertWarningMessage(this.translate.instant('accessApproval.noPermission', { name: this.nomeDa(funcionalidadeId) }));
                return false;

            case AcaoPermissao.AutorizarSolicitandoSenha:
            case AcaoPermissao.BloquearMasSolicitarSenha:
                return this.pedirSenha(funcionalidadeId, rota);

            default:
                return true;
        }
    }

    // "Módulo / Tela - Funcionalidade", o mesmo texto da grade de grupos, para o usuário achar a linha a alterar
    private textoDaFuncionalidade(funcionalidadeId: number): string {
        const chave = 'accessApproval.featureLabel.' + funcionalidadeId;
        const texto = this.translate.instant(chave);
        return texto === chave ? '' : texto;
    }

    private async pedirSenha(funcionalidadeId: number, rota: string): Promise<boolean> {
        const dialogRef = this.dialog.open(DialogAprovarAcessoComponent, {
            width: LARGURA_MODAL_AUTORIZAR_ACESSO,
            maxWidth: '95vw',
            disableClose: true,
            autoFocus: false, // o modal coloca o foco no campo certo; sem isso o MatDialog o move para o botão X
            data: {
                funcionalidade: this.textoDaFuncionalidade(funcionalidadeId),
                validar: (credenciais: CredenciaisAutorizacao) => this.solicitarAutorizacao(funcionalidadeId, credenciais)
            }
        });

        const autorizacao: TokenAutorizacao | undefined = await firstValueFrom(dialogRef.afterClosed());
        if (!autorizacao) return false;

        // A API só aceita a rota protegida com o token no cabeçalho (o ApiService o anexa)
        if (temRotaProtegida(funcionalidadeId)) this.tokenStore.guardar(funcionalidadeId, autorizacao);

        // Guarda a rota da autorização: sair dela descarta a autorização e o token (inclusive o de gravação, CT49).
        // Tela e consulta valem enquanto o usuário estiver na rota; a gravação segue pedindo a senha a cada vez,
        // pois jaAutorizada() olha o token (de uso único) e não este mapa.
        this.autorizadores.set(funcionalidadeId, rota);

        return true;
    }

    // A API valida o autorizado: senha, usuário ativo, ação 1 ou 2 dele e, na ação 4, que seja outra pessoa
    private async solicitarAutorizacao(funcionalidadeId: number, credenciais: CredenciaisAutorizacao): Promise<ResultadoAutorizacao> {
        const resposta = await this.autorizacaoService.validar(funcionalidadeId, credenciais);

        if (resposta?.success && resposta.data?.token) {
            return { autorizacao: resposta.data };
        }

        const falha = resposta as ApiErrorResponse | undefined;

        // A API devolve só o código da recusa; o texto vem do pt.json
        const mensagem = mensagemDoCodigoDeAutorizacao(this.translate, falha?.messageCode, this.nomeDa(funcionalidadeId));
        if (mensagem) {
            return { erro: mensagem, tentativasEsgotadas: falha?.messageCode === CodigoAutorizacaoWeb.TentativasEsgotadas };
        }

        // Qualquer outra falha (5xx, timeout, código desconhecido) é genérica; vai para o Sentry só com a mensagem e o stack trace
        registrarFalhaDeAutorizacao(undefined, 'Falha ao validar a autorização');
        return { erro: this.translate.instant('accessApproval.validationError') };
    }
}
